import React, {useState, useEffect} from 'react'
import TableCustom, {PaginationType, TableColumn} from 'src/components/elements/TableCustom/TableCustom'
import Filters, {FilterItem} from '../../Filters/Filters'
import {ImagesStyle} from './ImageStyle'
import apiClient from 'src/services/apiClient'
import {BODY_PARTS_OPTIONS, STATUS} from 'src/constants/constants'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import {Snackbar, Typography} from '@mui/material'
import {mdiImageSearchOutline} from '@mdi/js'
import ImageDialog from '../../ImageDialog/ImageDialog'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import {FormattedMessage} from 'react-intl'
import CompareImagesDialog from '../../CompareImagesDialog/CompareImagesDialog'
import {useAppSelector} from 'src/hooks'

const Images: React.FC<{
  patientId: string,
}> = ({
  patientId
}) => {
  const [filters, setFilters] = useState([])
  const [pagination, setPagination] = useState<PaginationType>({
    page: 0,
    rowsPerPage: 10,
    order: '-date'
  })
  const [data, setData] = useState<any>([])
  const [count, setCount] = useState<number>(0)

  const [status, setStatus] = useState<any>(undefined)
  const [statusCount, setStatusCount] = useState<any>(undefined)
  const [selectedRows, setSelectedRows] = useState<any>([])
  const [selectedImagesData, setSelectedImagesData] = useState<Record<string, any>[]>([])
  const [selectedImage, setSelectedImage] = useState<any>(null)
  const [openImageDialog, setOpenImageDialog] = useState<boolean>(false)
  const [compareModalOpen, setCompareModalOpen] = useState<boolean>(false)

  const onSelectionChange = (rows: any) => {
    setSelectedRows(rows)

    const currentPageSelectedImages = data.filter((image: Record<string, any>) => rows.includes(image._id))
    const updatedSelectedImages = selectedImagesData.filter((image: Record<string, any>) => rows.includes(image._id))

    currentPageSelectedImages.forEach((image: Record<string, any>) => {
      if (!updatedSelectedImages.find(existing => existing._id === image._id)) {
        updatedSelectedImages.push(image)
      }
    })

    setSelectedImagesData(updatedSelectedImages)
  }

  const fetchData = async () => {
    const fetchedData = await apiClient.getPatientImages(patientId, filters, pagination, setStatus) ?? []
    const parsedData = fetchedData.map((item: Record<string, any>) => ({
      ...item,
      bodyPart: BODY_PARTS_OPTIONS.find(option => option.value === item.bodyPart)?.label || item.bodyPart
    }))
    setData(parsedData)
  }

  const fetchDataCount = async () => {
    const fetchedDataCount = await apiClient.getPatientImagesCount(patientId, filters, setStatusCount) ?? []
    setCount(fetchedDataCount)
  }

  useEffect(() => {
    fetchData()
    fetchDataCount()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, filters, pagination])

  const imagesColumnOptions = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.imagesConfiguration?.columnOptions
  )

  const DEFAULT_COLUMNS: TableColumn[] = [
    {
      id: 'bodyPart',
      label: 'PARTE DEL CORPO'
    },
    {
      id: 'plannedDate',
      label: 'DATA ATTIVITÀ',
      type: 'date',
      format: 'ddd DD MMM YYYY'

    }
  ]

  const columns: TableColumn[] = imagesColumnOptions?.length > 0 ? imagesColumnOptions : DEFAULT_COLUMNS

  const filterConfig: FilterItem[] = [
    {
      id: 'date',
      label: 'Data',
      type: 'rangePicker'
    },
    {
      id: 'bodyPart',
      label: 'Parte del corpo',
      type: 'select',
      options: BODY_PARTS_OPTIONS
    }
  ]

  const onSubmitFilters = (newFilters: any) => {
    setFilters(newFilters)
    setPagination(prev => ({...prev, page: 0}))
  }

  return (
    <ImagesStyle>
      <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px'}}>
        <PrimaryButton
          disabled={selectedRows.length < 2}
          key='compare'
          onClick={() => setCompareModalOpen(true)}
        >
          <FormattedMessage id='compare' />
        </PrimaryButton>
        <Filters config={filterConfig} key='filters' onSubmit={onSubmitFilters} />
      </div>
      <TableCustom
        actions={[
          {
            id: 'form',
            label: 'Visualizza immagine',
            icon: mdiImageSearchOutline,
            action: (row: any) => {
              setSelectedImage(row)
              setOpenImageDialog(true)
            }
          }
        ]}
        columns={columns}
        enableMultiSelect
        isLoading={status === STATUS.PENDING}
        onPaginationChange={setPagination}
        onSelectionChange={onSelectionChange}
        pagination={pagination}
        rows={data}
        selectedRows={selectedRows}
        totalElements={count}
      />
      {openImageDialog && selectedImage &&
        <ImageDialog
          onClose={() => {
            setOpenImageDialog(false)
            setSelectedImage(null)
          }}
          record={selectedImage}
        />}

      {compareModalOpen && selectedRows.length >= 2 &&
        <CompareImagesDialog
          images={selectedImagesData}
          onClose={() => {
            setCompareModalOpen(false)
            setSelectedRows([])
            setSelectedImagesData([])
          }}
        />}
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
        open={status === STATUS.REJECTED || statusCount === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom onClose={() => { setStatus(undefined); setStatusCount(undefined) }} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {'Errore nel caricare i dati.'}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </ImagesStyle>
  )
}

export default Images
